import el from "@cypherpotato/el";

const TAB_SIZE = 4;
const TAB_CHAR = "    ";

/**
 * Editor de código baseado em textarea com syntax highlighting via Prism.js
 * Implementa keymaps estilo VS Code
 */
export function CodeEditor({ value, language, placeholder, height, props, wrapperProps, editorProps }) {
    let textarea;
    let highlightCode;
    let lineNumbers;
    let container;

    // Histórico para undo/redo
    const history = {
        past: [],
        future: [],
        maxSize: 100
    };

    function saveToHistory(content, selectionStart, selectionEnd) {
        history.past.push({ content, selectionStart, selectionEnd });
        if (history.past.length > history.maxSize) {
            history.past.shift();
        }
        history.future = [];
    }

    function undo() {
        if (history.past.length === 0) return;
        const current = {
            content: textarea.value,
            selectionStart: textarea.selectionStart,
            selectionEnd: textarea.selectionEnd
        };
        history.future.push(current);

        const prev = history.past.pop();
        textarea.value = prev.content;
        textarea.setSelectionRange(prev.selectionStart, prev.selectionEnd);
        updateHighlight();
    }

    function redo() {
        if (history.future.length === 0) return;
        const current = {
            content: textarea.value,
            selectionStart: textarea.selectionStart,
            selectionEnd: textarea.selectionEnd
        };
        history.past.push(current);

        const next = history.future.pop();
        textarea.value = next.content;
        textarea.setSelectionRange(next.selectionStart, next.selectionEnd);
        updateHighlight();
    }

    function getLanguageClass(lang) {
        if (!lang) return "language-plaintext";
        const langMap = {
            "js": "javascript",
            "ts": "typescript",
            "py": "python",
            "rb": "ruby",
            "cs": "csharp",
            "cpp": "cpp",
            "c": "c",
            "html": "markup",
            "xml": "markup",
            "svg": "markup",
            "md": "markdown",
            "sh": "bash",
            "shell": "bash",
            "yml": "yaml"
        };
        const mapped = langMap[lang.toLowerCase()] || lang.toLowerCase();
        return `language-${mapped}`;
    }

    function escapeHtml(text) {
        return text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }

    function updateHighlight() {
        const code = textarea.value;
        const langClass = getLanguageClass(language);
        const prismLang = langClass.replace("language-", "");

        if (!code?.length && placeholder) {
            highlightCode.innerHTML = `<span style="opacity: .5">${placeholder}</span>`;
            updateLineNumbers();
            return;
        }

        highlightCode.className = langClass;

        let highlightedHtml;
        if (window.Prism?.languages[prismLang]) {
            // Usa Prism.highlight() diretamente para evitar manipulação do DOM
            highlightedHtml = window.Prism.highlight(code, window.Prism.languages[prismLang], prismLang);
        } else {
            highlightedHtml = escapeHtml(code);
        }

        // Adiciona um espaço extra no final para scroll correto quando termina com \n
        if (code.endsWith("\n")) {
            highlightedHtml += " ";
        }

        highlightCode.innerHTML = highlightedHtml;

        // Atualiza números de linha
        updateLineNumbers();
    }

    function updateLineNumbers() {
        const lines = textarea.value.split("\n").length;
        const numbers = [];
        for (let i = 1; i <= lines; i++) {
            numbers.push(`<span>${i}</span>`);
        }
        lineNumbers.innerHTML = numbers.join("");
    }

    function getLineIndent(line) {
        const match = line.match(/^(\s*)/);
        return match ? match[1] : "";
    }

    function getCurrentLine() {
        const start = textarea.selectionStart;
        const text = textarea.value;
        const lineStart = text.lastIndexOf("\n", start - 1) + 1;
        const lineEnd = text.indexOf("\n", start);
        return {
            text: text.substring(lineStart, lineEnd === -1 ? text.length : lineEnd),
            start: lineStart,
            end: lineEnd === -1 ? text.length : lineEnd
        };
    }

    function insertText(text, selectStart = null, selectEnd = null) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const before = textarea.value.substring(0, start);
        const after = textarea.value.substring(end);

        saveToHistory(textarea.value, start, end);

        textarea.value = before + text + after;
        const newPos = selectStart !== null ? start + selectStart : start + text.length;
        const newEnd = selectEnd !== null ? start + selectEnd : newPos;
        textarea.setSelectionRange(newPos, newEnd);
        updateHighlight();
        triggerInput();
    }

    function triggerInput() {
        if (props?.onInput) {
            props.onInput({ target: { value: textarea.value } });
        }
    }

    function handleTab(e) {
        e.preventDefault();
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;

        if (e.shiftKey) {
            // Shift+Tab - remove indentação (funciona com ou sem seleção)
            const lineStart = text.lastIndexOf("\n", start - 1) + 1;
            const lineEnd = text.indexOf("\n", end - 1);
            const selectedLines = text.substring(lineStart, lineEnd === -1 ? text.length : lineEnd);
            const lines = selectedLines.split("\n");

            saveToHistory(text, start, end);

            const unindented = lines.map(line => {
                if (line.startsWith(TAB_CHAR)) {
                    return line.substring(TAB_SIZE);
                } else if (line.startsWith("\t")) {
                    return line.substring(1);
                }
                return line.replace(/^ {1,4}/, "");
            }).join("\n");

            const lengthDiff = selectedLines.length - unindented.length;
            textarea.value = text.substring(0, lineStart) + unindented + text.substring(lineEnd === -1 ? text.length : lineEnd);

            // Ajusta a posição do cursor
            const newStart = Math.max(lineStart, start - Math.min(TAB_SIZE, start - lineStart));
            const newEnd = Math.max(newStart, end - lengthDiff);
            textarea.setSelectionRange(newStart, start === end ? newStart : newEnd);

            updateHighlight();
            triggerInput();
        } else if (start === end) {
            // Sem seleção - insere tab
            insertText(TAB_CHAR);
        } else {
            // Com seleção - indenta linhas
            const lineStart = text.lastIndexOf("\n", start - 1) + 1;
            const lineEnd = text.indexOf("\n", end - 1);
            const selectedLines = text.substring(lineStart, lineEnd === -1 ? text.length : lineEnd);
            const lines = selectedLines.split("\n");

            saveToHistory(text, start, end);

            const indented = lines.map(line => TAB_CHAR + line).join("\n");
            textarea.value = text.substring(0, lineStart) + indented + text.substring(lineEnd === -1 ? text.length : lineEnd);
            textarea.setSelectionRange(lineStart, lineStart + indented.length);

            updateHighlight();
            triggerInput();
        }
    }

    function handleEnter(e) {
        e.preventDefault();
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;

        const lineStart = text.lastIndexOf("\n", start - 1) + 1;
        const currentLine = text.substring(lineStart, start);
        const indent = getLineIndent(currentLine);

        // Verifica se precisa aumentar indentação (após { [ ( :)
        const trimmedLine = currentLine.trimEnd();
        const lastChar = trimmedLine[trimmedLine.length - 1];
        const shouldIndent = ["{", "[", "(", ":"].includes(lastChar);

        // Verifica se o próximo caractere é um fechamento de bloco
        const nextChar = text[end];
        const isClosingBracket = ["}", "]", ")"].includes(nextChar);

        // Se estamos entre um abre e fecha bloco (ex: {|})
        if (shouldIndent && isClosingBracket) {
            const newIndent = indent + TAB_CHAR;
            // Insere nova linha com indent aumentado, cursor fica aqui
            // E outra linha com o indent original para o bracket de fechamento
            insertText("\n" + newIndent + "\n" + indent, newIndent.length + 1, newIndent.length + 1);
        } else {
            const newIndent = shouldIndent ? indent + TAB_CHAR : indent;
            insertText("\n" + newIndent);
        }
    }

    function handleBackspace(e) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        if (start !== end) return; // Deixa o comportamento padrão para seleção

        const text = textarea.value;
        const lineStart = text.lastIndexOf("\n", start - 1) + 1;
        const beforeCursor = text.substring(lineStart, start);

        // Se estiver deletando espaços no início da linha, deleta um tab inteiro
        if (beforeCursor.length > 0 && beforeCursor.trim() === "") {
            const spacesToDelete = beforeCursor.length % TAB_SIZE || TAB_SIZE;
            if (spacesToDelete > 1 && beforeCursor.length >= spacesToDelete) {
                e.preventDefault();
                saveToHistory(text, start, end);
                textarea.value = text.substring(0, start - spacesToDelete) + text.substring(start);
                textarea.setSelectionRange(start - spacesToDelete, start - spacesToDelete);
                updateHighlight();
                triggerInput();
            }
        }
    }

    function handleBrackets(e, openChar, closeChar) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        if (start !== end) {
            // Envolve seleção com brackets
            e.preventDefault();
            const selected = textarea.value.substring(start, end);
            insertText(openChar + selected + closeChar, 1, 1 + selected.length);
        } else {
            // Auto-fecha bracket
            e.preventDefault();
            insertText(openChar + closeChar, 1, 1);
        }
    }

    function handleCloseBracket(e, closeChar) {
        const start = textarea.selectionStart;
        const text = textarea.value;

        // Se o próximo caractere já é o closing bracket, apenas move o cursor
        if (text[start] === closeChar) {
            e.preventDefault();
            textarea.setSelectionRange(start + 1, start + 1);
        }
    }

    function handleQuote(e, quoteChar) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;

        // Se o próximo caractere já é a mesma quote, apenas move
        if (start === end && text[start] === quoteChar) {
            e.preventDefault();
            textarea.setSelectionRange(start + 1, start + 1);
            return;
        }

        if (start !== end) {
            // Envolve seleção
            e.preventDefault();
            const selected = text.substring(start, end);
            insertText(quoteChar + selected + quoteChar, 1, 1 + selected.length);
        } else {
            // Auto-fecha quote
            e.preventDefault();
            insertText(quoteChar + quoteChar, 1, 1);
        }
    }

    function duplicateLine(e) {
        e.preventDefault();
        const { text: currentLine, start: lineStart, end: lineEnd } = getCurrentLine();

        saveToHistory(textarea.value, textarea.selectionStart, textarea.selectionEnd);

        const cursorOffset = textarea.selectionStart - lineStart;
        const newValue = textarea.value.substring(0, lineEnd) + "\n" + currentLine + textarea.value.substring(lineEnd);
        textarea.value = newValue;

        const newCursorPos = lineEnd + 1 + cursorOffset;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        updateHighlight();
        triggerInput();
    }

    function deleteLine(e) {
        e.preventDefault();
        const text = textarea.value;
        const start = textarea.selectionStart;

        let lineStart = text.lastIndexOf("\n", start - 1) + 1;
        let lineEnd = text.indexOf("\n", start);
        if (lineEnd === -1) lineEnd = text.length;

        saveToHistory(text, start, textarea.selectionEnd);

        // Inclui a quebra de linha na deleção
        if (lineEnd < text.length) {
            lineEnd++;
        } else if (lineStart > 0) {
            lineStart--;
        }

        textarea.value = text.substring(0, lineStart) + text.substring(lineEnd);
        textarea.setSelectionRange(lineStart, lineStart);
        updateHighlight();
        triggerInput();
    }

    function moveLine(e, direction) {
        e.preventDefault();
        const text = textarea.value;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        const lines = text.split("\n");
        let startLine = text.substring(0, start).split("\n").length - 1;
        let endLine = text.substring(0, end).split("\n").length - 1;

        if (direction === -1 && startLine === 0) return;
        if (direction === 1 && endLine === lines.length - 1) return;

        saveToHistory(text, start, end);

        // Move as linhas
        const targetLine = direction === -1 ? startLine - 1 : endLine + 1;
        const movingLines = lines.splice(startLine, endLine - startLine + 1);

        if (direction === -1) {
            lines.splice(targetLine, 0, ...movingLines);
        } else {
            lines.splice(targetLine - movingLines.length + 1, 0, ...movingLines);
        }

        textarea.value = lines.join("\n");

        // Restaura seleção
        const newStartLine = startLine + direction;
        const newEndLine = endLine + direction;

        let newStart = lines.slice(0, newStartLine).join("\n").length + (newStartLine > 0 ? 1 : 0);
        let newEnd = lines.slice(0, newEndLine + 1).join("\n").length;

        textarea.setSelectionRange(newStart, newEnd);
        updateHighlight();
        triggerInput();
    }

    function selectLine(e) {
        e.preventDefault();
        const text = textarea.value;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        let lineStart = text.lastIndexOf("\n", start - 1) + 1;
        let lineEnd = text.indexOf("\n", end);
        if (lineEnd === -1) lineEnd = text.length;
        else lineEnd++;

        textarea.setSelectionRange(lineStart, lineEnd);
    }

    function commentLine(e) {
        e.preventDefault();
        const text = textarea.value;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        const lineStart = text.lastIndexOf("\n", start - 1) + 1;
        const lineEnd = text.indexOf("\n", end - 1);
        const selectedText = text.substring(lineStart, lineEnd === -1 ? text.length : lineEnd);
        const lines = selectedText.split("\n");

        // Determina o comentário baseado na linguagem
        const commentPrefix = getCommentPrefix();

        saveToHistory(text, start, end);

        // Verifica se todas as linhas já estão comentadas
        const allCommented = lines.every(line => line.trimStart().startsWith(commentPrefix));

        let newLines;
        if (allCommented) {
            // Remove comentários
            newLines = lines.map(line => {
                const trimmed = line.trimStart();
                const indent = line.substring(0, line.length - trimmed.length);
                if (trimmed.startsWith(commentPrefix + " ")) {
                    return indent + trimmed.substring(commentPrefix.length + 1);
                } else if (trimmed.startsWith(commentPrefix)) {
                    return indent + trimmed.substring(commentPrefix.length);
                }
                return line;
            });
        } else {
            // Adiciona comentários
            newLines = lines.map(line => {
                const trimmed = line.trimStart();
                const indent = line.substring(0, line.length - trimmed.length);
                return indent + commentPrefix + " " + trimmed;
            });
        }

        const newText = text.substring(0, lineStart) + newLines.join("\n") + text.substring(lineEnd === -1 ? text.length : lineEnd);
        textarea.value = newText;
        textarea.setSelectionRange(lineStart, lineStart + newLines.join("\n").length);
        updateHighlight();
        triggerInput();
    }

    function getCommentPrefix() {
        const langComments = {
            "javascript": "//",
            "typescript": "//",
            "js": "//",
            "ts": "//",
            "python": "#",
            "py": "#",
            "ruby": "#",
            "rb": "#",
            "bash": "#",
            "shell": "#",
            "sh": "#",
            "yaml": "#",
            "yml": "#",
            "css": "/*",
            "html": "<!--",
            "xml": "<!--",
            "csharp": "//",
            "cs": "//",
            "java": "//",
            "c": "//",
            "cpp": "//",
            "go": "//",
            "rust": "//",
            "php": "//",
            "sql": "--",
            "lua": "--"
        };
        return langComments[language?.toLowerCase()] || "//";
    }

    function handleKeyDown(e) {
        const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
        const modKey = isMac ? e.metaKey : e.ctrlKey;

        // Tab
        if (e.key === "Tab") {
            handleTab(e);
            return;
        }

        // Enter
        if (e.key === "Enter" && !e.shiftKey) {
            handleEnter(e);
            return;
        }

        // Backspace
        if (e.key === "Backspace") {
            handleBackspace(e);
            return;
        }

        // Brackets auto-close
        if (e.key === "(" && !modKey) {
            handleBrackets(e, "(", ")");
            return;
        }
        if (e.key === "[" && !modKey) {
            handleBrackets(e, "[", "]");
            return;
        }
        if (e.key === "{" && !modKey) {
            handleBrackets(e, "{", "}");
            return;
        }

        // Close brackets - skip if already there
        if (e.key === ")") {
            handleCloseBracket(e, ")");
            return;
        }
        if (e.key === "]") {
            handleCloseBracket(e, "]");
            return;
        }
        if (e.key === "}") {
            handleCloseBracket(e, "}");
            return;
        }

        // Quotes
        if (e.key === '"' && !modKey) {
            handleQuote(e, '"');
            return;
        }
        if (e.key === "'" && !modKey) {
            handleQuote(e, "'");
            return;
        }
        if (e.key === "`" && !modKey) {
            handleQuote(e, "`");
            return;
        }

        // Ctrl/Cmd + Z - Undo
        if (modKey && e.key === "z" && !e.shiftKey) {
            e.preventDefault();
            undo();
            return;
        }

        // Ctrl/Cmd + Shift + Z or Ctrl + Y - Redo
        if ((modKey && e.key === "z" && e.shiftKey) || (modKey && e.key === "y")) {
            e.preventDefault();
            redo();
            return;
        }

        // Ctrl/Cmd + D - Duplicate line
        if (modKey && e.key === "d") {
            duplicateLine(e);
            return;
        }

        // Ctrl/Cmd + Shift + K - Delete line
        if (modKey && e.shiftKey && e.key === "K") {
            deleteLine(e);
            return;
        }

        // Alt + Up/Down - Move line
        if (e.altKey && e.key === "ArrowUp") {
            moveLine(e, -1);
            return;
        }
        if (e.altKey && e.key === "ArrowDown") {
            moveLine(e, 1);
            return;
        }

        // Ctrl/Cmd + L - Select line
        if (modKey && e.key === "l") {
            selectLine(e);
            return;
        }

        // Ctrl/Cmd + / - Toggle comment
        if (modKey && e.key === "/") {
            commentLine(e);
            return;
        }

        // Ctrl/Cmd + A - Select all (let default behavior, but ensure focus stays)
        if (modKey && e.key === "a") {
            return;
        }
    }

    function handleInput(e) {
        // Só salva no histórico se foi uma entrada normal (não tratada por handlers especiais)
        if (!e.isComposing) {
            saveToHistory(textarea.value, textarea.selectionStart, textarea.selectionEnd);
        }
        updateHighlight();
        triggerInput();
    }

    // Cria os elementos
    lineNumbers = el(".code-editor-line-numbers", { "aria-hidden": "true" });

    highlightCode = el("code", { className: getLanguageClass(language) });
    const highlightPre = el("pre.code-editor-highlight", { "aria-hidden": "true" }, highlightCode);

    textarea = el("textarea.code-editor-textarea", {
        value: value || "",
        spellcheck: false,
        autocomplete: "off",
        autocapitalize: "off",
        autocorrect: "off",
        "data-gramm": "false",
        onKeyDown: handleKeyDown,
        onInput: handleInput,
        placeholder: props?.placeholder || "",
        style: {
            resize: "none"
        },
        ...editorProps
    });

    container = el(".code-editor[form-slot]", { id: props?.id, style: { height } },
        el(".code-editor-container",
            lineNumbers,
            el(".code-editor-content",
                highlightPre,
                textarea
            )
        ),
        wrapperProps,
        {
            onClick(event) {
                if (event.target === container) {
                    textarea.focus();
                }
            },
            $properties: {
                value: {
                    get() {
                        return textarea.value;
                    },
                    set(val) {
                        textarea.value = val;
                        updateHighlight();
                    }
                },
                language: {
                    get() {
                        return language;
                    },
                    set(val) {
                        language = val;
                        updateHighlight();
                    }
                }
            },
            $functions: {
                focus() {
                    textarea.focus();
                },
                blur() {
                    textarea.blur();
                },
                getSelection() {
                    return textarea.value.substring(textarea.selectionStart, textarea.selectionEnd);
                },
                setSelection(start, end) {
                    textarea.setSelectionRange(start, end);
                }
            },
            mount() {
                updateHighlight();
            }
        }
    );

    return container;
}